import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-creations/artifacts/cf3sam7y_Untitled%20design.png";

const bandstandImages = [
  "https://customer-assets.emergentagent.com/job_green-creations/artifacts/lz12ktgu_bandstand.png",
  "https://customer-assets.emergentagent.com/job_green-creations/artifacts/6sw0he8r_bandstand%201.png",
  "https://customer-assets.emergentagent.com/job_green-creations/artifacts/g5mpr0hd_bandstand%202.png",
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

export default function Structures() {
  return (
    <div data-testid="structures-page">
      {/* Page Header */}
      <section className="py-20 md:py-28 bg-[#EAE5D9]" data-testid="structures-page-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-8"
          >
            <div className="flex-1">
              <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-4">
                Collection
              </p>
              <h1 className="font-heading text-5xl sm:text-7xl tracking-tighter font-light text-[#222522]">
                Structures
              </h1>
              <p className="font-body text-base md:text-lg text-[#5C635B] mt-6 max-w-2xl leading-relaxed">
                Garden structures, shelters, and outdoor builds that&apos;ll make your neighbours pop their heads over the fence for a better look. All handbuilt from reclaimed materials — because why buy new when old has so much more character?
              </p>
            </div>
            <img
              src={LOGO_URL}
              alt="Small Feet"
              className="h-28 md:h-36 w-auto object-contain rounded flex-shrink-0"
              style={{ mixBlendMode: "multiply" }}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex gap-4 mt-8"
          >
            <a
              href="https://www.ebay.co.uk/usr/small_feet"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A5A3F] text-[#F9F8F5] text-sm font-body tracking-wider uppercase hover:bg-[#2d4832] transition-all hover:translate-y-[-1px] hover:shadow-lg"
              data-testid="shop-ebay-link"
            >
              Shop on eBay
              <ExternalLink size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Bandstand Feature Section */}
      <section className="py-24 md:py-32" data-testid="bandstand-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={0}
            variants={fadeUp}
            className="mb-10"
          >
            <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-3">
              Featured Build
            </p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
              The Bandstand
            </h2>
            <p className="font-body text-base text-[#5C635B] mt-5 max-w-2xl leading-relaxed">
              This one&apos;s a bit special. Built entirely by hand from reclaimed timber, 
              salvaged diamond-leaded windows (yes, really), and repurposed roofing. The 
              windows flood it with light, there&apos;s a raised deck made from old boards, 
              and every plank has lived a previous life somewhere else. It&apos;s basically 
              a garden pub without the bar tab.
            </p>
          </motion.div>

          {/* Bandstand Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {/* Hero image - full exterior */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-12"
            >
              <div className="gallery-item h-80 md:h-[32rem] relative rounded overflow-hidden" data-testid="bandstand-hero">
                <img
                  src={bandstandImages[0]}
                  alt="The Bandstand — exterior view"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">The Bandstand</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Full exterior — reclaimed timber frame with salvaged windows</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Interior shot */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-7"
            >
              <div className="gallery-item h-72 md:h-[26rem] relative rounded overflow-hidden" data-testid="bandstand-interior">
                <img
                  src={bandstandImages[1]}
                  alt="The Bandstand — interior"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Interior View</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Reclaimed furniture, diamond-leaded windows, and salvaged decking</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Second exterior angle */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={2}
              variants={fadeUp}
              className="md:col-span-5"
            >
              <div className="gallery-item h-72 md:h-[26rem] relative rounded overflow-hidden" data-testid="bandstand-angle">
                <img
                  src={bandstandImages[2]}
                  alt="The Bandstand — side angle"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Side View</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Pitched roof and open-front design with raised deck</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Franken'shed Feature Section */}
      <section className="py-24 md:py-32 bg-[#EAE5D9]" data-testid="frankenshed-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={0}
            variants={fadeUp}
            className="mb-10"
          >
            <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-3">
              Featured Build
            </p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
              The Franken&apos;shed
            </h2>
            <p className="font-body text-base text-[#5C635B] mt-5 max-w-2xl leading-relaxed">
              We called it the Franken&apos;shed because, well, look at it. It&apos;s gloriously 
              wonky, completely unique, and made from whatever reclaimed bits and bobs we 
              could get our hands on. Built for a client&apos;s lakeside property as a fishing 
              shed — no two panels match, the cladding is a patchwork of salvaged timber, 
              and it&apos;s got more character than most people we know.
            </p>
          </motion.div>

          {/* Franken'shed Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-12"
            >
              <div className="gallery-item h-80 md:h-[32rem] relative rounded overflow-hidden bg-[#EAE5D9]" data-testid="frankenshed-hero">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/fq4g8nvg_small%20feet%20frankenshed.avif"
                  alt="The Franken'shed — lakeside view"
                  className="w-full h-full object-contain"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">The Franken&apos;shed</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Lakeside fishing shed — built from entirely reclaimed materials</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-6"
            >
              <div className="gallery-item h-72 md:h-[26rem] relative rounded overflow-hidden" data-testid="frankenshed-angle-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/o2ejzhw6_small%20feet%20frakenshed%202.avif"
                  alt="The Franken'shed — detail view"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Detail View</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Mismatched reclaimed cladding and salvaged timber</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={2}
              variants={fadeUp}
              className="md:col-span-6"
            >
              <div className="gallery-item h-72 md:h-[26rem] relative rounded overflow-hidden" data-testid="frankenshed-angle-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/pbajz2ka_small%20feet%20frakenshed%203.avif"
                  alt="The Franken'shed — side angle"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Side Angle</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Every panel unique — nothing goes to waste</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Handbuilt Sheds Section */}
      <section className="py-24 md:py-32" data-testid="sheds-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={0}
            variants={fadeUp}
            className="mb-10"
          >
            <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-3">
              Handbuilt Sheds
            </p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
              Bespoke Garden Sheds
            </h2>
            <p className="font-body text-base text-[#5C635B] mt-5 max-w-2xl leading-relaxed">
              Not your average B&amp;Q special. These two were built completely from scratch 
              using reclaimed bits from start to finish. One&apos;s got a quirky barrel roof, 
              an arched plank door, and enough charm to make your shed-loving neighbour weep. 
              The other goes more classic — pitched corrugated roof, featherboard cladding, 
              and it sits on a reclaimed pallet deck because even the floor deserves a 
              second life.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-6"
            >
              <div className="gallery-item h-[26rem] md:h-[36rem] relative rounded overflow-hidden bg-[#EAE5D9]" data-testid="shed-barrel-roof">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/2bitkig8_shed%20style%202%20%3A%203.png"
                  alt="Handbuilt shed — barrel roof with arched door"
                  className="w-full h-full object-contain"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Barrel Roof Shed</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Curved felt roof and arched plank door</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-6"
            >
              <div className="gallery-item h-[26rem] md:h-[36rem] relative rounded overflow-hidden bg-[#EAE5D9]" data-testid="shed-pitched-roof">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/2neh0mp6_shed%20sytle%201%20%3A%202.png"
                  alt="Handbuilt shed — pitched roof with pallet deck"
                  className="w-full h-full object-contain"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Pitched Roof Shed</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Corrugated roof, featherboard cladding, pallet deck</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
}
